<script setup lang="ts">
export interface IBaseSearchInputProps {
  /**
   * Default to v-model
   */
  modelValue?: string;
}

const props = defineProps<IBaseSearchInputProps>();

const { t } = useI18n();
const appConfig = useRuntimeConfig().public;
const emit = defineEmits(["update:modelValue", "clear"]);
const searchValue = useVModel(props, "modelValue", emit);

const deleteValue = () => emit("clear");

const isMenuExpanded = ref(false);
const searchInputRef = ref(null);

const mostCommonSportTypesList = computed(() => appConfig.APP_CUSTOMER.most_common_sport_types_list);
const sportTypesOptions = computed(() => mostCommonSportTypesList.value.map(sport => ({ title: t(`sport_types.${sport}`), value: sport })));
const isSportTypeDropdownVisible = computed(() => mostCommonSportTypesList.value.length > 1);
const onCloseDropdown = () => isMenuExpanded.value = false;
const onClickDropdown = () => isMenuExpanded.value = true;

onClickOutside(searchInputRef, () => onCloseDropdown());
</script>

<template>
  <div ref="searchInputRef" class="searchInput">
    <BaseInput
      v-model="searchValue"
      root-class="hidden lg:block"
      class="font-medium"
      name="search"
      search
      autocomplete="off"
      :placeholder="t('labels.search')"
      @click="onClickDropdown"
      @clear="deleteValue"
      @update:model-value="onCloseDropdown"
    />
    <div v-if="isSportTypeDropdownVisible" class="searchInput__menu" :class="[{ searchInput__menu_open: isMenuExpanded }]">
      <div class="text-secondary font-medium text-sm w-full">
        {{ t('labels.search_by_sport_type') }}
      </div>
      <BaseChip v-for="sport in sportTypesOptions" :key="sport.value" class="!px-2 !py-1 !m-0 bg-[var(--base-card-bgSecondaryColor)]">
        <BaseButton
          name="header-search-sport-type-options"
          type="icon"
          tag="nuxt-link"
          :to="{ path: '/search/', query: { sport_type: [sport.value] } }"
          @click.capture="onCloseDropdown"
        >
          <div class="flex gap-2 items-center justify-center">
            <IconSportType :sport-type="sport.value" class="text-[var(--base-icon-color)]" />
            {{ sport.title }}
          </div>
        </BaseButton>
      </BaseChip>
    </div>
  </div>
</template>

<style lang="scss">
.searchInput {
  @apply relative h-full flex items-center ;

  .searchInput__menu {
    @apply hidden overflow-hidden absolute z-40 right-0 w-full p-4 h-auto;
    top: calc(100% + 8px);
    background-color: var(--base-card-bgColor);
    border-radius: var(--base-border-radius);
    box-shadow: var(--base-box-shadow);

    &.searchInput__menu_open {
      @apply flex gap-4 flex-wrap;
    }
  }

  .baseInput__inner {
    background-color: var(--base-baseInput-background-color);
  }
}
</style>
